import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap, catchError, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MarketingSessionService } from '../analytics/marketing-session.service';
import { WizardDataService } from '../wizard/wizard-data.service';

export interface AuthResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    providerType: string;
  };
}

export interface UserProfile {
  id: string;
  email: string;
  providerType: string;
}

export interface SignUpRequest {
  email: string;
  password: string;
}

export interface SignInRequest {
  email: string;
  password: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly marketingSessionService = inject(MarketingSessionService);
  private readonly wizardDataService = inject(WizardDataService);

  private readonly apiUrl = environment.apiUrl;
  private readonly tokenKey = 'jwt_token';

  private readonly isAuthenticated$ = new BehaviorSubject<boolean>(this.hasToken());

  isAuthenticated(): Observable<boolean> {
    return this.isAuthenticated$.asObservable();
  }

  signUp(request: SignUpRequest, redirectUrl?: string): Observable<AuthResponse> {
    const params = redirectUrl ? `?redirectUrl=${encodeURIComponent(redirectUrl)}` : '';
    return this.http.post<AuthResponse>(`${this.apiUrl}/auth/signup${params}`, request).pipe(
      tap(response => this.handleAuthSuccess(response))
    );
  }

  signIn(request: SignInRequest, redirectUrl?: string): Observable<AuthResponse> {
    const params = redirectUrl ? `?redirectUrl=${encodeURIComponent(redirectUrl)}` : '';
    return this.http.post<AuthResponse>(`${this.apiUrl}/auth/signin${params}`, request).pipe(
      tap(response => this.handleAuthSuccess(response))
    );
  }

  signInWithGoogle(redirectUrl?: string): void {
    const frontendUrl = window.location.origin;
    const params = new URLSearchParams();
    params.set('frontendUrl', frontendUrl);
    if (redirectUrl) {
      params.set('redirectUrl', redirectUrl);
    }
    window.location.href = `${this.apiUrl}/auth/google?${params.toString()}`;
  }

  signInWithMicrosoft(redirectUrl?: string): void {
    const frontendUrl = window.location.origin;
    const params = new URLSearchParams();
    params.set('frontendUrl', frontendUrl);
    if (redirectUrl) {
      params.set('redirectUrl', redirectUrl);
    }
    window.location.href = `${this.apiUrl}/auth/microsoft?${params.toString()}`;
  }

  logout(): Observable<void> {
    return of(undefined).pipe(
      tap(() => {
        localStorage.removeItem(this.tokenKey);
        this.isAuthenticated$.next(false);
        this.marketingSessionService.clearSession();
        this.wizardDataService.reset();
      })
    );
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
    this.isAuthenticated$.next(true);
  }

  validateToken(token: string): Observable<boolean> {
    if (!token || token.trim() === '') {
      return of(false);
    }

    // Validate token by calling a protected endpoint
    return this.http.get(`${this.apiUrl}/auth/profile`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      tap(() => {
        // Token is valid, store it
        this.setToken(token);
      }),
      map(() => true),
      catchError((error) => {
        console.error('Invalid token provided:', error);
        return of(false);
      })
    );
  }

  getProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${this.apiUrl}/auth/profile`);
  }

  private hasToken(): boolean {
    return !!localStorage.getItem(this.tokenKey);
  }

  private handleAuthSuccess(response: AuthResponse): void {
    this.setToken(response.accessToken);
    this.marketingSessionService.linkToCurrentUser();
  }
}

